import { API_BASE_URL } from '../lib/axiosConfig';

/**
 * Fetches the candidate resume via the download-resume endpoint and returns a blob URL
 * together with the actual content-type reported by the server.
 *
 * Callers use `isPdf` to decide between inline preview (PDF) and a download link (DOC/DOCX).
 * The server's content-type is the ground truth — it is derived from the stored file URL,
 * NOT from the original upload filename (which would be stale after LibreOffice conversion).
 *
 * @param {string} candidateId
 * @returns {Promise<{ url: string, contentType: string, isPdf: boolean } | null>}
 */
export async function getResumeViewUrl(candidateId) {
  if (!candidateId || typeof candidateId !== 'string') return null;
  const token = localStorage.getItem('token');
  if (!token) return null;

  try {
    const resp = await fetch(
      `${API_BASE_URL}/api/screenings/candidates/${candidateId}/download-resume`,
      {
        credentials: 'include',
        cache: 'no-store',
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (!resp.ok) return null;

    const ct = (resp.headers.get('content-type') || '').toLowerCase();
    if (ct.includes('text/html')) return null;

    const buf = await resp.arrayBuffer();
    if (!buf || buf.byteLength === 0) return null;

    const blob = new Blob([buf], { type: ct || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const isPdf = ct.includes('pdf');
    return { url, contentType: ct, isPdf };
  } catch (_) {}

  return null;
}

/**
 * Hiring-manager public review link variant of getResumeViewUrl.
 *
 * @param {string} shareCode
 * @param {string} candidateId
 * @returns {Promise<{ url: string, contentType: string, isPdf: boolean } | null>}
 */
export async function getPublicResumeViewUrl(shareCode, candidateId) {
  if (!shareCode || !candidateId || typeof candidateId !== 'string') return null;
  const enc = encodeURIComponent(String(shareCode).trim());

  try {
    const resp = await fetch(
      `${API_BASE_URL}/api/screenings/public/candidate-review/${enc}/resume/${candidateId}`,
      {
        credentials: 'include',
        cache: 'no-store',
      }
    );
    if (!resp.ok) return null;

    const ct = (resp.headers.get('content-type') || '').toLowerCase();
    if (ct.includes('text/html')) return null;

    const buf = await resp.arrayBuffer();
    if (!buf || buf.byteLength === 0) return null;

    const blob = new Blob([buf], { type: ct || 'application/octet-stream' });
    const url = window.URL.createObjectURL(blob);
    const isPdf = ct.includes('pdf');
    return { url, contentType: ct, isPdf };
  } catch (_) {}

  return null;
}
